import React from 'react'
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material'
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  MeetingRoom as MeetingRoomIcon,
  Email as EmailIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import { CompanyWithStats } from '../../types'
import { getStatusColor, getBillingTierColor, normalizeBillingTier } from '../../types'
import { alpha, useTheme } from '@mui/material/styles'

interface CompanyCardProps {
  company: CompanyWithStats
  onViewUsers: (company: CompanyWithStats) => void
  onEditCompany: (company: CompanyWithStats) => void
  onToggleStatus?: (company: CompanyWithStats, isActive: boolean) => void
  onSelectCompany?: (company: CompanyWithStats) => void
  onManageRooms?: (company: CompanyWithStats) => void
  onManageEmailTemplates?: (company: CompanyWithStats) => void
  onRemoveCompany?: (company: CompanyWithStats) => void
  isPlatformAdmin?: boolean
}

export const CompanyCard: React.FC<CompanyCardProps> = ({
  company,
  onViewUsers,
  onEditCompany,
  onToggleStatus,
  onSelectCompany,
  onManageRooms,
  onManageEmailTemplates,
  onRemoveCompany,
  isPlatformAdmin = false,
}) => {
  const theme = useTheme()
  const primaryHex = theme.palette.primary.main
  // `deleting` means a delete stopped part-way (e.g. Stripe failed). Delete stays available so
  // the admin can retry; reactivating a half-deleted company is not offered. [SC-409]
  const deletionIncomplete = (company.status as string) === 'deleting'
  return (
    <Paper
      onClick={() => onSelectCompany && onSelectCompany(company)}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        bgcolor: company.is_active ? 'background.paper' : 'action.hover',
        backgroundImage: 'none',
        borderRadius: '1rem',
        border: '2px solid',
        borderColor: company.is_active ? 'divider' : alpha(theme.palette.text.primary, 0.12),
        display: 'flex',
        padding: 0,
        height: '11.25rem',
        position: 'relative',
        overflow: 'hidden',
        opacity: company.is_active ? 1 : 0.7,
        boxShadow: company.is_active ? theme.shadows[2] : theme.shadows[1],
        '&:hover': {
          transform: company.is_active ? 'translateY(-0.25rem)' : 'none',
          boxShadow: company.is_active ? theme.shadows[8] : theme.shadows[2],
          borderColor: company.is_active ? 'primary.main' : alpha(theme.palette.text.secondary, 0.4),
          '& .company-icon': {
            transform: company.is_active ? 'scale(1.05)' : 'none',
          }
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: company.is_active 
            ? `linear-gradient(90deg, ${getBillingTierColor(company.billing_tier || 'free')} 0%, ${alpha(primaryHex, 0.1)} 100%)`
            : `linear-gradient(90deg, ${theme.palette.error.main} 0%, ${alpha(theme.palette.error.main, 0.15)} 100%)`,
          opacity: 0.8
        }
      }}
    >
      {/* Left Column - Indicators */}
      <Box sx={{ 
        width: '80px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '1rem 0.75rem',
        bgcolor: 'action.hover',
        backgroundImage: 'none',
        borderRight: '1px solid',
        borderColor: 'divider',
        position: 'relative'
      }}>
        {/* Company Icon */}
        <Box
          className="company-icon"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: company.is_active 
              ? alpha(primaryHex, 0.1)
              : alpha(theme.palette.text.secondary, 0.12),
            color: company.is_active 
              ? 'primary.main'
              : 'text.secondary',
            borderRadius: '0.75rem',
            width: '3rem',
            height: '3rem',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            marginBottom: '0.75rem'
          }}
        >
          <BusinessIcon fontSize="medium" />
        </Box>

        {/* Tier Indicator */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          gap: '8px',
          flex: 1,
          justifyContent: 'center'
        }}>
          {company.billing_tier ? (
            <Chip
              label={normalizeBillingTier(company.billing_tier).charAt(0).toUpperCase() + normalizeBillingTier(company.billing_tier).slice(1)}
              size="small"
              sx={{
                bgcolor: company.is_active 
                  ? alpha(getBillingTierColor(company.billing_tier), 0.12)
                  : alpha(theme.palette.text.secondary, 0.12),
                color: company.is_active 
                  ? getBillingTierColor(company.billing_tier)
                  : 'text.secondary',
                fontSize: '0.75rem',
                height: '1.5rem',
                fontWeight: 600,
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                border: company.is_active 
                  ? `1px solid ${alpha(getBillingTierColor(company.billing_tier), 0.35)}`
                  : `1px solid ${alpha(theme.palette.text.secondary, 0.25)}`,
                borderRadius: '0.5rem',
                minWidth: '3.125rem',
                '& .MuiChip-label': {
                  padding: '0 0.375rem'
                }
              }}
            />
          ) : (
            <Chip
              label="Free"
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.75rem',
                height: '1.5rem',
                color: company.is_active 
                  ? 'text.secondary'
                  : 'text.disabled',
                borderColor: company.is_active 
                  ? alpha(theme.palette.text.secondary, 0.35)
                  : alpha(theme.palette.text.disabled, 0.35),
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                fontWeight: 600,
                borderRadius: '0.5rem',
                minWidth: '3.125rem',
                '& .MuiChip-label': {
                  padding: '0 0.375rem'
                }
              }}
            />
          )}

          {/* Users Indicator */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: '4px'
          }}>
            <PeopleIcon sx={{ 
              fontSize: '1rem', 
              color: company.is_active 
                ? 'text.secondary'
                : 'text.disabled',
              opacity: 0.8
            }} />
            <Typography 
              variant="caption" 
              sx={{ 
                fontSize: '0.75rem',
                color: company.is_active 
                  ? 'text.primary'
                  : 'text.secondary',
                fontWeight: 600,
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                textAlign: 'center',
                lineHeight: 1
              }}
            >
              {company.total_users || 0}
            </Typography>
          </Box>

          {/* Rooms Indicator */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: '4px'
          }}>
            <BusinessIcon sx={{ 
              fontSize: '1rem', 
              color: company.is_active 
                ? 'text.secondary'
                : 'text.disabled',
              opacity: 0.8
            }} />
            <Typography 
              variant="caption" 
              sx={{ 
                fontSize: '0.75rem',
                color: company.is_active 
                  ? 'text.primary'
                  : 'text.secondary',
                fontWeight: 600,
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                textAlign: 'center',
                lineHeight: 1
              }}
            >
              {company.total_rooms || 0}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Right Column - Content */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        padding: '16px',
        justifyContent: 'space-between'
      }}>
        {/* Header with company name and status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography 
            variant="h6" 
            fontWeight={600} 
            sx={{ 
              color: company.is_active ? 'text.primary' : 'text.secondary', 
              flex: 1, 
              mr: 1,
              fontSize: '1.125rem',
              lineHeight: 1.25,
              fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
              letterSpacing: '-0.01em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}
          >
            {company.name}
          </Typography>
          {deletionIncomplete ? (
            <Chip
              label="Deletion incomplete"
              color="warning"
              size="small"
              data-testid="company-card-deletion-incomplete"
              sx={{
                minWidth: 'fit-content',
                fontSize: '0.75rem',
                height: '1.5rem',
                fontWeight: 600,
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                borderRadius: '0.5rem',
              }}
            />
          ) : (
            <Chip
              label={company.is_active ? company.status : "DISABLED"}
              color={company.is_active ? getStatusColor(company.status) : "error"}
              size="small"
              sx={{ 
                minWidth: 'fit-content',
                fontSize: '0.75rem',
                height: '1.5rem',
                fontWeight: 500,
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                borderRadius: '0.5rem',
                ...(company.is_active ? {} : {
                  bgcolor: alpha(theme.palette.error.main, 0.12),
                  color: 'error.main',
                  fontWeight: 600,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
                })
              }}
            />
          )}
        </Box>
        
        {/* Domain information */}
        <Typography 
          variant="body2" 
          sx={{ 
            mb: 2,
            fontSize: '0.875rem',
            color: 'text.secondary',
            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
            letterSpacing: '0.025em',
            lineHeight: 1.5
          }}
        >
          {company.domain}
        </Typography>

        {/* Actions section - organized in rows */}
        <Box sx={{ 
          mt: 'auto',
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}>
          {/* Primary Actions Row */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            mb: 1,
            justifyContent: 'space-between'
          }}>
            {/* Left side - Management actions */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="View Users">
                <IconButton
                  size="small"
                  onClick={() => onViewUsers(company)}
                  sx={{ 
                    color: 'info.main',
                    bgcolor: alpha(theme.palette.info.main, 0.12),
                    borderRadius: '6px',
                    width: '28px',
                    height: '28px',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.info.main, 0.2),
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <PeopleIcon sx={{ fontSize: '14px' }} />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Edit Company">
                <IconButton
                  size="small"
                  onClick={() => onEditCompany(company)}
                  sx={{ 
                    color: 'primary.main',
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    borderRadius: '6px',
                    width: '28px',
                    height: '28px',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <EditIcon sx={{ fontSize: '14px' }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Right side - Status toggle */}
            {isPlatformAdmin && onToggleStatus && !deletionIncomplete && (
              <Tooltip title={company.is_active ? "Suspend company" : "Reactivate company"}>
                <IconButton
                  size="small"
                  aria-label={company.is_active ? "Suspend company" : "Reactivate company"}
                  data-testid="company-card-toggle-status"
                  // The card itself is clickable (select company); without this the toggle also
                  // fired that handler and its "Selected company" toast.
                  onClick={(e) => { e.stopPropagation(); onToggleStatus(company, !company.is_active) }}
                  sx={{ 
                    color: company.is_active ? 'success.main' : 'error.main',
                    bgcolor: company.is_active 
                      ? alpha(theme.palette.success.main, 0.12)
                      : alpha(theme.palette.error.main, 0.12),
                    borderRadius: '6px',
                    width: '28px',
                    height: '28px',
                    '&:hover': {
                      bgcolor: company.is_active 
                        ? alpha(theme.palette.success.main, 0.2)
                        : alpha(theme.palette.error.main, 0.2),
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {company.is_active ? (
                    <CheckCircleIcon sx={{ fontSize: '14px' }} />
                  ) : (
                    <BlockIcon sx={{ fontSize: '14px' }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Secondary Actions Row - Admin only */}
          {isPlatformAdmin && (
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              {/* Left side - Additional management */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {onManageEmailTemplates && (
                  <Tooltip title="Email Templates">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); onManageEmailTemplates(company) }}
                      sx={{ 
                        color: 'warning.main',
                        bgcolor: alpha(theme.palette.warning.main, 0.12),
                        borderRadius: '6px',
                        width: '28px',
                        height: '28px',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.warning.main, 0.2),
                          transform: 'scale(1.05)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <EmailIcon sx={{ fontSize: '14px' }} />
                    </IconButton>
                  </Tooltip>
                )}
                
                {onManageRooms && (
                  <Tooltip title="Manage Rooms">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); onManageRooms(company) }}
                      sx={{ 
                        color: 'success.main',
                        bgcolor: alpha(theme.palette.success.main, 0.12),
                        borderRadius: '6px',
                        width: '28px',
                        height: '28px',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.success.main, 0.2),
                          transform: 'scale(1.05)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <MeetingRoomIcon sx={{ fontSize: '14px' }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {/* Right side - Destructive action */}
              {onRemoveCompany && (
                <Tooltip title="Delete company">
                  <IconButton
                    size="small"
                    aria-label="Delete company"
                    data-testid="company-card-delete"
                    onClick={(e) => { e.stopPropagation(); onRemoveCompany(company) }}
                    sx={{ 
                      color: 'error.main',
                      bgcolor: alpha(theme.palette.error.main, 0.12),
                      borderRadius: '6px',
                      width: '28px',
                      height: '28px',
                      border: `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.error.main, 0.2),
                        borderColor: alpha(theme.palette.error.main, 0.5),
                        transform: 'scale(1.05)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: '14px' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  )
} 